import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck,
  Building2,
  Network,
  Lock,
  Server,
  Fingerprint,
  Radar,
  Layers,
} from "lucide-react";

/* ── Types ───────────────────────────────────────── */
export interface TimelineItem {
  id: string;
  control_code: string;
  finding_title: string;
  risk_level: string;
  priority_level: string;
  timeframe: string;
  recommended_action: string;
  csv_domain: string | null;
  nist_function: string | null;
}

interface Props {
  items: TimelineItem[];
  assessmentDate?: string; // ISO date string of assessment creation
}

/* ── Constants ───────────────────────────────────── */
const DOMAIN_ICONS: Record<string, React.ElementType> = {
  "Risk Management": ShieldCheck,
  Governance: Building2,
  "Supply Chain Management": Network,
  "Physical Security & Environmental Controls": Lock,
  "Asset Management": Server,
  "Identity-Centric Security": Fingerprint,
  "Zero Trust Architecture": Fingerprint,
  "Security Operations": Radar,
};

const RISK_DOT: Record<string, string> = {
  Critical: "bg-red-600",
  HIGH: "bg-destructive",
  High: "bg-destructive",
  MODERATE: "bg-yellow-500",
  Moderate: "bg-yellow-500",
  LOW: "bg-green-600",
  Low: "bg-green-600",
};

const RISK_BORDER: Record<string, string> = {
  Critical: "border-red-600/40",
  HIGH: "border-destructive/40",
  High: "border-destructive/40",
  MODERATE: "border-yellow-500/40",
  Moderate: "border-yellow-500/40",
  LOW: "border-green-600/40",
  Low: "border-green-600/40",
};

const TIMEFRAME_DAYS: Record<string, number> = {
  "30 days": 30,
  "30–60 days": 45,
  "60–90 days": 75,
  "90 days": 90,
  "90–120 days": 105,
  "90–180 days": 135,
  "180 days": 180,
  "365 days": 365,
};

/* ── Helpers ─────────────────────────────────────── */
function parseTimeframeDays(tf: string): number {
  return TIMEFRAME_DAYS[tf] ?? 90;
}

function getQuarterLabel(d: Date): string {
  const q = Math.ceil((d.getMonth() + 1) / 3);
  return `Q${q} ${d.getFullYear()}`;
}

function getMonthLabel(d: Date): string {
  return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

function getDomainIcon(domain: string | null): React.ElementType {
  if (!domain) return Layers;
  for (const [key, Icon] of Object.entries(DOMAIN_ICONS)) {
    if (domain.toLowerCase().includes(key.toLowerCase().slice(0, 8))) return Icon;
  }
  return Layers;
}

/* ── Component ───────────────────────────────────── */
export const RoadmapTimeline: React.FC<Props> = ({ items, assessmentDate }) => {
  const baseDate = useMemo(
    () => (assessmentDate ? new Date(assessmentDate) : new Date()),
    [assessmentDate]
  );

  // Derive milestones with target dates
  const milestones = useMemo(() => {
    return items.map((item) => {
      const days = parseTimeframeDays(item.timeframe);
      const target = new Date(baseDate);
      target.setDate(target.getDate() + days);
      return {
        ...item,
        targetDate: target,
        days,
        description: item.recommended_action || item.finding_title || "Remediation required",
      };
    }).sort((a, b) => a.targetDate.getTime() - b.targetDate.getTime());
  }, [items, baseDate]);

  // Group by quarter
  const quarters = useMemo(() => {
    const groups: Record<string, typeof milestones> = {};
    for (const m of milestones) {
      const key = getQuarterLabel(m.targetDate);
      if (!groups[key]) groups[key] = [];
      groups[key].push(m);
    }
    return Object.entries(groups);
  }, [milestones]);

  if (milestones.length === 0) {
    return (
      <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
        No milestones to display.
      </div>
    );
  }

  return (
    <div className="space-y-6 roadmap-timeline-print">
      {quarters.map(([quarter, qMilestones]) => (
        <div key={quarter} className="space-y-3">
          {/* Quarter Header */}
          <div className="flex items-center gap-3">
            <div className="bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">
              {quarter}
            </div>
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">
              {qMilestones.length} milestone{qMilestones.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Milestone Cards */}
          <div className="relative pl-6">
            {/* Vertical line */}
            <div className="absolute left-[11px] top-0 bottom-0 w-px bg-border" />

            <div className="space-y-3">
              {qMilestones.map((m, idx) => {
                const DomainIcon = getDomainIcon(m.csv_domain);
                const dotColor = RISK_DOT[m.risk_level] ?? "bg-muted-foreground";
                const borderColor = RISK_BORDER[m.risk_level] ?? "border-border";

                return (
                  <div key={m.id} className="relative flex items-start gap-3">
                    {/* Dot on timeline */}
                    <div
                      className={`absolute -left-6 top-3 h-3 w-3 rounded-full ring-2 ring-background ${dotColor}`}
                    />

                    {/* Card */}
                    <div
                      className={`flex-1 rounded-lg border-l-4 ${borderColor} bg-card border border-border p-3 transition-colors hover:bg-accent/30`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <DomainIcon className="h-4 w-4 text-primary shrink-0" />
                          <span className="text-sm font-medium truncate">
                            {m.description}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0"
                          >
                            {m.control_code || "—"}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                            {getMonthLabel(m.targetDate)}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mt-1.5">
                        <Badge
                          className={`text-[10px] px-1.5 py-0 ${
                            RISK_DOT[m.risk_level]
                              ? `${RISK_DOT[m.risk_level]} text-white`
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {m.risk_level}
                        </Badge>
                        {m.csv_domain && (
                          <span className="text-[10px] text-muted-foreground">
                            {m.csv_domain}
                          </span>
                        )}
                        {m.priority_level && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {m.priority_level}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default RoadmapTimeline;
